export function HomeContent() {

  return (
    <div className="w-full space-y-16 py-8">
      {/* Hero Section */}
      <div className="text-center space-y-6 max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-foreground">
          Grow as a Photographer Through Feedback
        </h1>
        <p className="text-xl text-muted leading-relaxed">
          206mp is a photo community built for photographers who want to improve their craft.
          Get thoughtful feedback, share constructive comments, and curate your best work—all
          in a small, high-trust environment.
        </p>
      </div>

      {/* Why 206mp Section */}
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-semibold text-foreground mb-8 text-center">
          Why 206mp?
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="space-y-3">
            <div className="text-5xl">🎯</div>
            <h3 className="text-xl font-semibold text-foreground">Quality Over Quantity</h3>
            <p className="text-muted">
              Critiques are limited to 32 participants to ensure meaningful feedback from people
              you trust. No algorithms, no viral chasing—just genuine improvement.
            </p>
          </div>
          <div className="space-y-3">
            <div className="text-5xl">💡</div>
            <h3 className="text-xl font-semibold text-foreground">Actionable Feedback</h3>
            <p className="text-muted">
              Go beyond comments. Show suggested crops, lighting adjustments, and composition
              improvements with visual annotations and side-by-side comparisons.
            </p>
          </div>
          <div className="space-y-3">
            <div className="text-5xl">📸</div>
            <h3 className="text-xl font-semibold text-foreground">Full Resolution Support</h3>
            <p className="text-muted">
              Upload images of any size. View every detail with advanced zoom, pan, and EXIF
              metadata—because the details matter.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-surface-1 rounded-xl p-8 max-w-5xl mx-auto border border-border">
        <h2 className="text-3xl font-semibold text-foreground mb-8 text-center">
          How It Works
        </h2>
        <div className="space-y-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              1
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Create a Collection</h3>
              <p className="text-muted">
                Upload your photos and organize them into collections—portfolios, project sets,
                or albums you want feedback on.
              </p>
              {/* prompt: "A clean UI screenshot showing a grid of landscape photographs being arranged into a collection, modern dark theme interface, aspect ratio 16:9, 800x450px" */}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              2
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Request a Critique</h3>
              <p className="text-muted">
                Start a critique session on your collection. Invite specific photographers or
                open it to the community (up to 32 participants). You can even get AI feedback
                from specialized photography personas.
              </p>
              {/* prompt: "UI dialog showing critique session creation with participant selection, toggle for AI critiques, modern minimalist design, aspect ratio 16:9, 800x450px" */}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              3
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Give &amp; Receive Feedback</h3>
              <p className="text-muted">
                Write comments, draw annotations, suggest crops, propose lighting adjustments.
                See all feedback in context with side-by-side before/after comparisons.
              </p>
              {/* prompt: "Split-screen photo critique interface showing original photo on left and suggested crop/edit on right, with annotation tools visible, professional photography context, aspect ratio 16:9, 800x450px" */}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              4
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Grow &amp; Iterate</h3>
              <p className="text-muted">
                Apply the feedback, refine your work, and create new critique sessions to track
                your progress. Each collection can have multiple critique rounds for different
                types of feedback.
              </p>
              {/* prompt: "Visual timeline showing photo evolution with multiple critique iterations, progress indicators, clean modern UI, aspect ratio 16:9, 800x450px" */}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-semibold text-foreground mb-8 text-center">
          Powerful Features for Serious Photographers
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-surface-1 rounded-lg p-6 border border-border space-y-2">
            <h3 className="text-lg font-semibold text-foreground">Visual Critique Tools</h3>
            <p className="text-muted">
              Draw shapes, suggest crops, propose lighting adjustments. Show, don't just tell,
              with visual editing suggestions attached to your comments.
            </p>
          </div>

          <div className="bg-surface-1 rounded-lg p-6 border border-border space-y-2">
            <h3 className="text-lg font-semibold text-foreground">AI-Powered Feedback</h3>
            <p className="text-muted">
              Get instant critiques from AI personas specialized in composition, lighting,
              documentary photography, and more. Available 24/7 to supplement human feedback.
            </p>
          </div>

          <div className="bg-surface-1 rounded-lg p-6 border border-border space-y-2">
            <h3 className="text-lg font-semibold text-foreground">Magazine-Style Collections</h3>
            <p className="text-muted">
              Create beautiful, curated collections with drag-and-drop reordering and custom
              layouts. Perfect for portfolios, project showcases, or thematic albums.
            </p>
          </div>

          <div className="bg-surface-1 rounded-lg p-6 border border-border space-y-2">
            <h3 className="text-lg font-semibold text-foreground">Threaded Discussions</h3>
            <p className="text-muted">
              Every critique is a conversation. Reply to specific feedback, @-mention
              participants, and keep discussions organized and actionable.
            </p>
          </div>

          <div className="bg-surface-1 rounded-lg p-6 border border-border space-y-2">
            <h3 className="text-lg font-semibold text-foreground">Complete EXIF Data</h3>
            <p className="text-muted">
              View camera settings, lens info, exposure details, and more. Understand the
              technical decisions behind every shot with full metadata display.
            </p>
          </div>

          <div className="bg-surface-1 rounded-lg p-6 border border-border space-y-2">
            <h3 className="text-lg font-semibold text-foreground">High-Res Viewing</h3>
            <p className="text-muted">
              No file size limits. Upload massive images and view them at 100% with smooth
              zoom and pan. Your work deserves to be seen in full detail.
            </p>
          </div>
        </div>
      </div>

      {/* Community Values Section */}
      <div className="bg-primary/10 rounded-xl p-8 max-w-5xl mx-auto border border-primary/20">
        <h2 className="text-3xl font-semibold text-foreground mb-4 text-center">
          A Different Kind of Photo Community
        </h2>
        <p className="text-lg text-muted text-center max-w-3xl mx-auto mb-6">
          We're not building another social network. 206mp is designed for photographers who
          want genuine feedback and improvement, not likes and followers.
        </p>
        <ul className="space-y-3 max-w-2xl mx-auto text-muted">
          <li className="flex gap-3">
            <span className="text-primary flex-shrink-0">✓</span>
            <span><strong className="text-foreground">Small groups:</strong> 32-person limit ensures quality over quantity</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary flex-shrink-0">✓</span>
            <span><strong className="text-foreground">No viral mechanics:</strong> No trending pages, no follower counts</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary flex-shrink-0">✓</span>
            <span><strong className="text-foreground">Focus on craft:</strong> Tools for actionable feedback, not engagement metrics</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary flex-shrink-0">✓</span>
            <span><strong className="text-foreground">You own your work:</strong> Delete a photo, it's gone everywhere—no surprises</span>
          </li>
        </ul>
      </div>

      {/* CTA Section */}
      <div className="text-center space-y-6 max-w-3xl mx-auto">
        <h2 className="text-3xl font-semibold text-foreground">
          Ready to Level Up Your Photography?
        </h2>
        <p className="text-lg text-muted">
          Join 206mp and start getting the feedback you need to improve your craft.
          Create collections, request critiques, and grow as a photographer.
        </p>
        {/* Note: Add actual sign-up button/link when ready */}
        <div className="text-sm text-muted italic">
          Currently in active development. Check back soon!
        </div>
      </div>
    </div>
  );
}
